/**
 * PhotoViewerContext - Shared context for Edit and View pages
 *
 * Provides:
 * - Current photo state (selectedPhoto, metaDoc, faces)
 * - Display preferences (showVizImage, showCropMask)
 * - Permission flags (canEdit, canTag, canApprove, canDelete)
 * - Navigation helpers (next/prev photo)
 */

import { createContext, useContext } from 'react';

/**
 * Context for photo viewer state shared between Edit and View pages.
 * Provides photo data, display preferences, and permission-based UI hints.
 */
const PhotoViewerContext = createContext(null);

/**
 * Hook to access the PhotoViewer context.
 * Must be used within a PhotoViewerProvider.
 *
 * @returns {PhotoViewerContextValue} The photo viewer context value
 */
export function usePhotoViewerContext() {
  const context = useContext(PhotoViewerContext);
  if (!context) {
    throw new Error('usePhotoViewerContext must be used within a PhotoViewerProvider');
  }
  return context;
}

/**
 * Provider component for PhotoViewerContext.
 * Wraps components that need access to shared photo viewer state.
 */
export function PhotoViewerProvider({ value, children }) {
  return (
    <PhotoViewerContext.Provider value={value}>
      {children}
    </PhotoViewerContext.Provider>
  );
}

/**
 * @typedef {Object} PhotoViewerContextValue
 * @property {Object|null} selectedPhoto - The currently selected photo data
 * @property {string|null} selectedPhotoId - ID of the currently selected photo
 * @property {Object|null} metaDoc - Metadata document (crop coordinates, viz data)
 * @property {Array} faces - Array of faces detected in the photo
 * @property {Array} photos - List of photos in the sidebar
 * @property {boolean} loadingPhoto - Whether the photo is loading
 * @property {boolean} loadingList - Whether the photo list is loading
 *
 * Display preferences:
 * @property {boolean} showVizImage - Whether to show viz image (vs edited)
 * @property {Function} setShowVizImage - Toggle viz image
 * @property {boolean} showCropMask - Whether to show crop mask overlay
 * @property {Function} setShowCropMask - Toggle crop mask
 * @property {Object|null} imgBounds - Image element bounds for overlays
 * @property {Object|null} vizNaturalDims - Natural dimensions of viz image
 *
 * Permissions (hide buttons, don't disable):
 * @property {boolean} canEditPhotos - Can edit/crop photos
 * @property {boolean} canTagFaces - Can tag faces
 * @property {boolean} canApprovePhotos - Can mark done/skip
 * @property {boolean} canDeletePhotos - Can delete photos
 * @property {boolean} isReadOnly - Photo is read-only for current user
 *
 * Navigation:
 * @property {Function} selectPhoto - Select a photo by ID
 * @property {Function} getNextPhotoId - Get next photo ID
 * @property {Function} getPrevPhotoId - Get previous photo ID
 *
 * Computed values:
 * @property {Object|null} cropBox - Computed crop box from metadata
 * @property {string|null} currentVizUrl - URL for viz/preview image
 * @property {string|null} currentEditedUrl - URL for edited image
 */

export default PhotoViewerContext;
